/**
 * Chip registry.
 *
 * Everything that varies between Espressif targets lives here as data, so
 * supporting a new part is one entry in CHIPS rather than a change in the
 * flashing code. `imageChipId` is the value stored at byte 12..13 of an ESP
 * application image header and is how we tell what a .bin was actually built
 * for -- see binaryInfo.ts.
 */

export type ChipKey =
  | "esp32"
  | "esp32s2"
  | "esp32s3"
  | "esp32c2"
  | "esp32c3"
  | "esp32c6"
  | "esp32h2"
  | "esp32p4"
  | "esp8266";

export interface ChipProfile {
  key: ChipKey;
  /** Human label, matches how Espressif writes it. */
  name: string;
  /** esp_chip_id_t as embedded in an app image header. undefined = not carried in header (ESP8266). */
  imageChipId?: number;
  /** Where the second-stage bootloader belongs on a fresh chip. */
  bootloaderOffset: number;
  /** Partition table offset. ESP8266 (non-IDF Arduino builds) has none. */
  partitionTableOffset?: number;
  /** Arduino's OTA-data blob (boot_app0.bin). */
  otaDataOffset?: number;
  /** Default application offset for Arduino/IDF single-app layouts. */
  appOffset: number;
  /** Substrings that appear in esptool's detected chip description. */
  detectAliases: string[];
  /** Rough note shown in the UI when the chip is detected. */
  notes?: string;
}

const ESP32_FAMILY_DEFAULTS = {
  partitionTableOffset: 0x8000,
  otaDataOffset: 0xe000,
  appOffset: 0x10000,
} as const;

export const CHIPS: Record<ChipKey, ChipProfile> = {
  esp32: {
    key: "esp32",
    name: "ESP32",
    imageChipId: 0x0000,
    bootloaderOffset: 0x1000,
    ...ESP32_FAMILY_DEFAULTS,
    detectAliases: ["esp32-d0wd", "esp32-d2wd", "esp32-pico", "esp32-u4wdh", "esp32"],
  },
  esp32s2: {
    key: "esp32s2",
    name: "ESP32-S2",
    imageChipId: 0x0002,
    bootloaderOffset: 0x1000,
    ...ESP32_FAMILY_DEFAULTS,
    detectAliases: ["esp32-s2", "esp32s2"],
  },
  esp32s3: {
    key: "esp32s3",
    name: "ESP32-S3",
    imageChipId: 0x0009,
    bootloaderOffset: 0x0000,
    ...ESP32_FAMILY_DEFAULTS,
    detectAliases: ["esp32-s3", "esp32s3"],
    notes: "Native USB parts may re-enumerate after reset; that is normal.",
  },
  esp32c2: {
    key: "esp32c2",
    name: "ESP32-C2",
    imageChipId: 0x000c,
    bootloaderOffset: 0x0000,
    ...ESP32_FAMILY_DEFAULTS,
    detectAliases: ["esp32-c2", "esp32c2", "esp8684"],
  },
  esp32c3: {
    key: "esp32c3",
    name: "ESP32-C3",
    imageChipId: 0x0005,
    bootloaderOffset: 0x0000,
    ...ESP32_FAMILY_DEFAULTS,
    detectAliases: ["esp32-c3", "esp32c3"],
  },
  esp32c6: {
    key: "esp32c6",
    name: "ESP32-C6",
    imageChipId: 0x000d,
    bootloaderOffset: 0x0000,
    ...ESP32_FAMILY_DEFAULTS,
    detectAliases: ["esp32-c6", "esp32c6"],
  },
  esp32h2: {
    key: "esp32h2",
    name: "ESP32-H2",
    imageChipId: 0x0010,
    bootloaderOffset: 0x0000,
    ...ESP32_FAMILY_DEFAULTS,
    detectAliases: ["esp32-h2", "esp32h2"],
  },
  esp32p4: {
    key: "esp32p4",
    name: "ESP32-P4",
    imageChipId: 0x0012,
    bootloaderOffset: 0x2000,
    ...ESP32_FAMILY_DEFAULTS,
    detectAliases: ["esp32-p4", "esp32p4"],
  },
  esp8266: {
    key: "esp8266",
    name: "ESP8266",
    bootloaderOffset: 0x0000,
    appOffset: 0x0000,
    detectAliases: ["esp8266", "esp8285"],
    notes: "Arduino ESP8266 exports one combined image that is written at 0x0.",
  },
};

export const CHIP_LIST: ChipProfile[] = Object.values(CHIPS);

/** Match esptool's detected chip description ("ESP32-S3 (QFN56)...") to a profile. */
export function chipFromDetectedName(detected: string): ChipProfile | undefined {
  const haystack = detected.toLowerCase();
  // Longest alias first so "esp32-s3" wins over the bare "esp32" substring.
  const byAliasLength = CHIP_LIST.flatMap((chip) =>
    chip.detectAliases.map((alias) => ({ chip, alias })),
  ).sort((a, b) => b.alias.length - a.alias.length);

  return byAliasLength.find(({ alias }) => haystack.includes(alias))?.chip;
}

export function chipFromImageChipId(id: number): ChipProfile | undefined {
  return CHIP_LIST.find((chip) => chip.imageChipId === id);
}

export function formatOffset(offset: number): string {
  return `0x${offset.toString(16).toUpperCase().padStart(4, "0")}`;
}
